import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { getRandomEstasseguroAvatar } from "../../utils/estasseguroAvatar";
import { setGroup, updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
	postS5InvestmentProposal,
	deleteS5InvestmentProposal,
	getS5InvestmentSync,
} from "../../api/group";
import { modalConfirm, modalQuestionResolution } from "../../utils/logger";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import logoPryconsa from "../../assets/logo-pryconsa.png";
import logoGrefusa from "../../assets/logo-grefusa.png";
import logoPalacios from "../../assets/logo-palacios.png";
import "../StartGame.css";
import "../Scenario2/Scenario2.css";
import "./Scenario5.css";

function escapeHtml(text) {
	if (text == null) return "";
	return String(text)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#039;");
}

const SC5_SWAL_POPUP_CLASS = "modal-sg5 modal-sg5--cobega";

const POLL_MS = 2000;

const MSG_ROUND_NOT_OPEN =
	"Aún no se ha abierto la ronda de inversión. Debes esperar el resto de grupos que terminen.";
const MSG_WAITING_PROPOSAL =
	"Enviada la propuesta de inversión, esperando respuesta...";
const MSG_ALLIANCE =
	"No llegan al mínimo importe del tiquet de inversión. Por favor, creen alianzas con los demás grupos para llegar al tiquet mínimo.";

const COORDINATED_TITLE = "Se ha coordinado la inversión";
const MSG_SUBLINE =
	"Se esperan grandes beneficios de rentabilidad gracias a la inversión conjunta realizada";

const QUESTION_STEM =
	"Ha llegado el momento de poner a prueba tu criterio. Revisa el capital que has conseguido reunir. Ante ti se presentan tres posibles inversiones. Deberás escoger la que consideres más adecuada. Si no inviertes tu capital, lo perderás. Pero cuidado: la respuesta no está solo en los números. Para elegir, tendrás que comprender qué ha llevado a la familia Daurella a construir lo que hoy conocemos como COBEGA: visión, alianzas, adaptación y capacidad para anticiparse al futuro. Invierte con criterio. El legado está en juego.";

const INVESTMENTS = [
	{
		id: "pryconsa",
		name: "Grupo Pryconsa",
		logo: logoPryconsa,
		expectedReturn: "8% – 12%",
		sector: "Real estate, promoción residencial y gestión patrimonial inmobiliaria.",
		description:
			"Es una promotora y constructora española fundada en 1965, de capital privado/familiar, especializada en vivienda de obra nueva, urbanización de suelo, construcción, posventa, cooperativas, alquiler residencial y patrimonio inmobiliario. La tesis de inversión se apoyaría en su integración vertical —suelo, promoción, construcción y venta—, su larga trayectoria, su cartera de proyectos residenciales y su capacidad para diversificar hacia build-to-rent, socimis y expansión internacional selectiva.",
	},
	{
		id: "grefusa",
		name: "Grefusa",
		logo: logoGrefusa,
		expectedReturn: "10% – 15%",
		sector: "Snacks, frutos secos y alimentación de impulso.",
		description:
			"Dedicada a pipas, frutos secos, snacks y productos horneados, con marcas reconocidas en el canal gran consumo. La tesis estaría en su posicionamiento en consumo recurrente, innovación de producto, distribución nacional y posibilidad de crecimiento en snacks más saludables o expansión internacional selectiva.",
	},
	{
		id: "palacios",
		name: "Grupo Palacios Alimentación",
		logo: logoPalacios,
		expectedReturn: "9% – 13%",
		sector: "Alimentación premium preparada, cárnicos y platos refrigerados/congelados.",
		description:
			"Grupo español conocido por chorizo, tortillas, pizzas y soluciones alimentarias, con actividad industrial y presencia internacional. La tesis de inversión se basaría en marcas propias, capacidad productiva, productos de conveniencia, diversificación de categorías y crecimiento en foodservice/retail.",
	},
];

function escapeAttrUrl(url) {
	if (url == null) return "";
	return String(url).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

/** HTML del modal de éxito (logo + textos) según id de inversión acordada. */
function buildCoordinatedInvestmentSuccessHtml(choiceId) {
	const inv = INVESTMENTS.find((i) => i.id === choiceId);
	const name = inv?.name || "la empresa elegida";
	const logoUrl = inv?.logo || "";
	const safeName = escapeHtml(name);
	const src = escapeAttrUrl(logoUrl);
	return `<div class="sc5-invest-success-modal text-center px-2">
		<div class="sc5-invest-success-modal__logo-wrap mb-3">
			<img src="${src}" alt="" class="sc5-invest-success-modal__logo" decoding="async" />
		</div>
		<p class="txt22 txtBlue bold mb-2">Inversión efectuada en ${safeName}</p>
		<p class="txt18 txtBlue mb-0">${escapeHtml(MSG_SUBLINE)}</p>
	</div>`;
}

function Scenario5Quiz1() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.QUIZ5;
	const SCENARIO_ID = SCENARIOS.SCENARIO_5;

	const [selectedId, setSelectedId] = useState(null);
	const [isCompleted, setIsCompleted] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [pendingScore, setPendingScore] = useState(() => Number(group?.score || 0));
	const [seededBaseScore, setSeededBaseScore] = useState(false);

	useEffect(() => {
		if (seededBaseScore) return;
		if (!group) return;
		const s = Number(group?.score);
		if (!Number.isFinite(s)) return;
		setPendingScore(s);
		setSeededBaseScore(true);
	}, [group, seededBaseScore]);

	const gameIdForApi = String(group?.game?._id || group?.game || "");

	async function runPostAnswerSafe(totalTime) {
		try {
			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: 0,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO5_FIN,
				correct: true,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch(setGroup(serverGroup));
				}
				setIsCompleted(true);
				setIsSubmitting(false);
				navigate(ROUTES.SCENARIO5_FIN);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;
			const serverMsg = error?.response?.data?.message;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
					themePopupClass: SC5_SWAL_POPUP_CLASS,
				});
				navigate(ROUTES.SCENARIO5_FIN);
				return;
			}

			if (
				status === 409 &&
				(code === "E_INVESTMENT_NOT_LOCKED" ||
					code === "E_INVESTMENT_CHOICE_MISMATCH")
			) {
				await modalQuestionResolution({
					correct: false,
					question: "Inversión",
					textHtml: `<div class="txt24 txt-red bold">${escapeHtml(
						serverMsg ||
							"No se pudo registrar la inversión. Vuelve a intentarlo desde la pantalla de inversión."
					)}</div>`,
					customConfirmButtonHtml: "Aceptar",
					themePopupClass: SC5_SWAL_POPUP_CLASS,
				});
				setIsSubmitting(false);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC5_SWAL_POPUP_CLASS,
			});
			setIsSubmitting(false);
		}
	}

	async function openWaitingProposalModal(
		gameId,
		groupId,
		totalTime,
		failureBaseline
	) {
		const ignoreDismissRef = { current: false };
		const doneRef = { current: false };
		let pollTimer = null;

		const stopPoll = () => {
			if (pollTimer) {
				clearInterval(pollTimer);
				pollTimer = null;
			}
		};

		const maybeAlliance = async () => {
			ignoreDismissRef.current = true;
			stopPoll();
			Swal.close();
			await modalQuestionResolution({
				correct: false,
				question: "Inversión",
				textHtml: `<div class="txt24 txtBlue bold">${escapeHtml(MSG_ALLIANCE)}</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC5_SWAL_POPUP_CLASS,
			});
			try {
				await deleteS5InvestmentProposal(groupId);
			} catch {
				// ignorar error de red; el ranking puede seguir mostrando hasta reintento
			}
			setSelectedId(null);
			setIsSubmitting(false);
		};

		const maybeSuccess = async (lockedChoice) => {
			ignoreDismissRef.current = true;
			stopPoll();
			Swal.close();
			await modalQuestionResolution({
				correct: true,
				question: COORDINATED_TITLE,
				textHtml: buildCoordinatedInvestmentSuccessHtml(lockedChoice),
				customConfirmButtonHtml: "Continuar",
				themePopupClass: SC5_SWAL_POPUP_CLASS,
				color: "orange",
			});
			await runPostAnswerSafe(totalTime);
		};

		const pollOnce = async () => {
			if (doneRef.current) return;
			try {
				const { data } = await getS5InvestmentSync(gameId, groupId);
				if (data.lockedChoice) {
					doneRef.current = true;
					await maybeSuccess(data.lockedChoice);
					return;
				}
				if ((data.failureSeq || 0) > failureBaseline) {
					doneRef.current = true;
					await maybeAlliance();
				}
			} catch {
				// reintentar en el siguiente ciclo
			}
		};

		const result = await Swal.fire({
			html: `<div class="txt24 txtBlue bold">${escapeHtml(
				MSG_WAITING_PROPOSAL
			)}</div>`,
			showConfirmButton: false,
			showCancelButton: true,
			cancelButtonText: "Cancelar inversión",
			allowOutsideClick: false,
			allowEscapeKey: false,
			backdrop: "rgba(0,0,0, .7)",
			customClass: {
				popup: `modal-confirm-container pb-5 ${SC5_SWAL_POPUP_CLASS}`.trim(),
				cancelButton: "btn-main btn-main--swal-text bold shadow orange",
			},
			didOpen: () => {
				void pollOnce();
				pollTimer = setInterval(() => {
					void pollOnce();
				}, POLL_MS);
			},
			willClose: () => {
				stopPoll();
			},
		});

		if (
			!ignoreDismissRef.current &&
			result.isDismissed &&
			result.dismiss === Swal.DismissReason.cancel
		) {
			try {
				await deleteS5InvestmentProposal(groupId);
			} catch {
				// ignorar error de red al cancelar
			}
			setIsSubmitting(false);
		}
	}

	async function submitInvestment() {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		const choice = selectedId;
		if (!choice || !INVESTMENTS.some((i) => i.id === choice)) {
			setIsSubmitting(false);
			return;
		}

		const gid = group?._id;
		const gGame = gameIdForApi;
		if (!gid || !gGame) {
			await modalQuestionResolution({
				correct: false,
				question: "Inversión",
				textHtml: `<div class="txt24 txt-red bold">No se ha podido identificar el equipo. Vuelve a cargar la página.</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC5_SWAL_POPUP_CLASS,
			});
			setIsSubmitting(false);
			return;
		}

		let data;
		try {
			const res = await postS5InvestmentProposal(gid, { choice });
			data = res.data;
		} catch (attemptErr) {
			const status = attemptErr?.response?.status;
			const code = attemptErr?.response?.data?.code;
			const serverMsg = attemptErr?.response?.data?.message;

			if (status === 409 && code === "E_INVESTMENT_ROUND_NOT_OPEN") {
				await modalQuestionResolution({
					correct: false,
					question: "Ronda de inversión",
					textHtml: `<div class="txt24 txtBlue bold">${escapeHtml(
						serverMsg || MSG_ROUND_NOT_OPEN
					)}</div>`,
					customConfirmButtonHtml: "Aceptar",
					themePopupClass: SC5_SWAL_POPUP_CLASS,
				});
				setIsSubmitting(false);
				return;
			}

			if (status === 409 && code === "E_INVESTMENT_RESUME_MISMATCH") {
				await modalQuestionResolution({
					correct: false,
					question: "Inversión",
					textHtml: `<div class="txt24 txtBlue bold">${escapeHtml(
						serverMsg ||
							"Ya registraste la inversión. Debes elegir la misma empresa que acordasteis para volver a continuar."
					)}</div>`,
					customConfirmButtonHtml: "Aceptar",
					themePopupClass: SC5_SWAL_POPUP_CLASS,
				});
				setIsSubmitting(false);
				return;
			}

			if (status === 409 && code === "E_INVESTMENT_CHOICE_MISMATCH_LOCK") {
				await modalQuestionResolution({
					correct: false,
					question: "Inversión",
					textHtml: `<div class="txt24 txtBlue bold">${escapeHtml(
						serverMsg ||
							"La ronda está cerrada con otra empresa. Todos debéis coincidir en la misma opción."
					)}</div>`,
					customConfirmButtonHtml: "Aceptar",
					themePopupClass: SC5_SWAL_POPUP_CLASS,
				});
				setIsSubmitting(false);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo enviar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al enviar la propuesta. Inténtalo de nuevo.</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC5_SWAL_POPUP_CLASS,
			});
			setIsSubmitting(false);
			return;
		}

		if (data.code === "WAITING_OTHERS") {
			const baseline = Number(data.failureSeq) || 0;
			await openWaitingProposalModal(gGame, gid, totalTime, baseline);
			return;
		}

		if (data.code === "FAILED_CONSENSUS") {
			await modalQuestionResolution({
				correct: false,
				question: "Inversión",
				textHtml: `<div class="txt24 txtBlue bold">${escapeHtml(
					data.message || MSG_ALLIANCE
				)}</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC5_SWAL_POPUP_CLASS,
			});
			try {
				await deleteS5InvestmentProposal(gid);
			} catch {
				// ignorar error de red
			}
			setSelectedId(null);
			setIsSubmitting(false);
			return;
		}

		if (
			data.code === "SUCCESS" ||
			data.code === "ALREADY_LOCKED" ||
			data.code === "RESUME_COMPLETE"
		) {
			const locked = data.choice || data.lockedChoice;
			if (!locked) {
				setIsSubmitting(false);
				return;
			}
			await modalQuestionResolution({
				correct: true,
				question: COORDINATED_TITLE,
				textHtml: buildCoordinatedInvestmentSuccessHtml(locked),
				customConfirmButtonHtml: "Continuar",
				themePopupClass: SC5_SWAL_POPUP_CLASS,
				color: "orange",
			});
			await runPostAnswerSafe(totalTime);
			return;
		}

		await modalQuestionResolution({
			correct: false,
			question: "Inversión",
			textHtml: `<div class="txt24 txt-red bold">Respuesta inesperada del servidor. Inténtalo de nuevo.</div>`,
			customConfirmButtonHtml: "Aceptar",
			themePopupClass: SC5_SWAL_POPUP_CLASS,
		});
		setIsSubmitting(false);
	}

	function handleInvertir() {
		if (!selectedId) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="swal-estasseguro-shell"><div class="swal-estasseguro-inner"><img src="${getRandomEstasseguroAvatar()}" alt="" class="estasseguro swal-estasseguro-img" /></div></div><div class="text-center txtTitulos mt-3 orange">Selecciona una inversión antes de invertir.</div>`,
				themePopupClass: SC5_SWAL_POPUP_CLASS,
			});
			return;
		}

		if (!INVESTMENTS.some((i) => i.id === selectedId)) return;

		modalConfirm({
			estasseguro: getRandomEstasseguroAvatar(),
			color: "orange",
			themePopupClass: SC5_SWAL_POPUP_CLASS,
		}).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);
			submitInvestment();
		});
	}

	return (
		<div className="start-game-page start-game-page--welcome sc2-context-page sc2-quiz-page sc2-quiz1-brand sc5-invest-quiz">
			<div className="start-game-inner start-game-inner--prep5">
				<div className="sg5-scroll">
					<header className="sg5-hero sc5-invest-hero">
						<div className="sc5-invest-hero__row">
							<h1 className="sg5-title sc5-invest-hero__title">Tu inversión</h1>
							<span className="start-game-badge sg5-badge sc5-invest-hero__badge">
								<span className="start-game-badge-dot" aria-hidden />
								Escenario 4 · Inversión
							</span>
						</div>
						<p className="sg5-lead sc2-quiz-stem">{QUESTION_STEM}</p>
					</header>

					<div className="sc2-quiz-toolbar sc5-invest-toolbar">
						<p className="sc2-quiz-hint-line mb-0">
							<IconHandClick
								size={28}
								color="rgba(239, 239, 237, 0.85)"
								stroke={1.25}
								className="sc2-quiz-hand me-2"
							/>
							<span>Selecciona una de las tres inversiones y pulsa Invertir.</span>
						</p>
					</div>

					<div className="sc5-invest-grid">
						{INVESTMENTS.map((inv) => {
							const isSelected = selectedId === inv.id;
							return (
								<div
									key={inv.id}
									className={`sc5-invest-column ${isSelected ? "sc5-invest-column--selected" : ""}`}
								>
									<button
										type="button"
										className={`sc5-invest-card ${isSelected ? "sc5-invest-card--selected" : ""}`}
										onClick={() => setSelectedId(inv.id)}
										aria-pressed={isSelected ? "true" : "false"}
										aria-label={`Invertir en ${inv.name}`}
									>
										<div className="sc5-invest-card__logo-wrap">
											<img
												src={inv.logo}
												alt=""
												className="sc5-invest-card__logo"
												decoding="async"
											/>
										</div>
										<h2 className="sc5-invest-card__title">{inv.name}</h2>
										<p className="sc5-invest-card__sector">{inv.sector}</p>
										<p className="sc5-invest-card__body">{inv.description}</p>
									</button>
									<div className="sc5-invest-rent-card">
										<p className="sc5-invest-rent-card__label">Rentabilidad esperada</p>
										<p className="sc5-invest-rent-card__value">{inv.expectedReturn}</p>
									</div>
								</div>
							);
						})}
					</div>
				</div>

				<div className="sg5-actions sc2-quiz-footer sc5-invest-footer">
					<CheckButton
						text="Invertir"
						onClick={handleInvertir}
						disabled={isCompleted || isSubmitting}
						color="orange"
						loading={isSubmitting}
						bare
						className="sc2-quiz-check-wrap sc5-invest-cta-wrap"
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />
		</div>
	);
}

export default Scenario5Quiz1;
